"use client";

/**
 * `TextField` — the canonical single-line text input for the form
 * engine. Reads its rhf instance from `useFormContext` so callers can
 * drop a `<TextField field={...} />` anywhere inside a `<FormProvider>`
 * (including Phase G repeater rows) without prop-drilling the context.
 *
 * Behaviour notes for this Phase B implementation:
 *
 * - `condition` checking is handled centrally by the FieldRenderer in
 *   Phase G. Here we ONLY check it as a defence-in-depth no-op when a
 *   caller renders the field directly (without going through the
 *   renderer). When the condition predicate is present and returns
 *   `false`, we return `null` so the control disappears entirely. We
 *   intentionally evaluate the condition against `getValues()` (a
 *   one-shot read) rather than `useWatch` because reactive visibility
 *   wiring lands in Phase G with the rest of the conditional plumbing.
 *
 * - `required` is NOT inferred from the zod schema in this Phase B
 *   wiring — schema introspection is too brittle. The shell renders the
 *   asterisk when `field.requiredPermission` is unset and the schema
 *   has a non-optional shape, which we also defer to Phase G. For now
 *   the asterisk simply does not render; the schema enforces validation.
 *
 * - Errors come from `formState.errors[field.name]?.message`. rhf typing
 *   uses a `FieldErrors` map keyed by field name, so we read with a
 *   bracket access and a defensive cast. The shell hides the error span
 *   when there is none.
 */

import * as React from "react";
import { Input } from "@aio/platform-ui";
import { useFormContext } from "../form-context";
import type { TextFieldDef } from "../types";
import { FieldShell } from "./field-shell";
import { readErrorMessage } from "./read-error";
import { resolveFieldPath } from "./path";

export interface TextFieldProps {
  /** Declarative field config from the platform-form types. */
  field: TextFieldDef;
  /**
   * Optional dotted path prefix passed by {@link RepeaterField} so the
   * field registers under its row's nested path. When omitted the field
   * registers against `field.name` as a top-level form key.
   */
  pathPrefix?: string;
}

export function TextField({ field, pathPrefix }: TextFieldProps): React.ReactElement | null {
  const { form } = useFormContext();

  // Defense-in-depth condition check. When false, render nothing — Phase
  // G replaces this with a centralised reactive visibility wrapper.
  if (field.condition && !field.condition(form.getValues())) {
    return null;
  }

  const path = resolveFieldPath(field.name, pathPrefix);
  const errorMessage = readErrorMessage(form.formState.errors, path);

  return (
    <FieldShell
      htmlFor={path}
      label={field.label}
      {...(field.description !== undefined ? { description: field.description } : {})}
      {...(errorMessage !== undefined ? { error: errorMessage } : {})}
    >
      <Input
        id={path}
        type="text"
        aria-invalid={errorMessage !== undefined ? true : undefined}
        // We deliberately do NOT spread `disabled: undefined` because
        // exactOptionalPropertyTypes treats explicit `undefined` as a
        // distinct value from "absent". Branch on truthiness.
        {...(field.disabled ? { disabled: true } : {})}
        {...(field.placeholder !== undefined
          ? { placeholder: field.placeholder }
          : {})}
        {...(field.autoComplete !== undefined
          ? { autoComplete: field.autoComplete }
          : {})}
        {...(field.maxLength !== undefined
          ? { maxLength: field.maxLength }
          : {})}
        {...form.register(path)}
      />
    </FieldShell>
  );
}

